import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles, ArrowRight, X, HelpCircle } from "lucide-react";

const STORAGE_KEY = "nexus_creator_studio_tour_v1";

// Each step focuses on ONE tab in the CreatorStudio shell. The `focusTab` is
// applied when the step opens so the target is guaranteed to be in the DOM.
const STEPS = [
  {
    id: "welcome",
    title: "Welcome to your Studio",
    body: "Every draft, template, brand kit and export lives here. 30-second tour?",
    target: '[data-testid="studio-tabs"]',
    focusTab: "recent",
    accent: "from-nexus-pink to-nexus-purple",
    duration: "30 seconds",
  },
  {
    id: "recent",
    title: "Recent projects",
    body: "Every save auto-appears here, ready to resume from any device. Hover a tile to jump back into the editor.",
    target: '[data-testid="studio-tab-recent"]',
    focusTab: "recent",
    accent: "from-nexus-blue to-nexus-purple",
  },
  {
    id: "new",
    title: "Start a new project",
    body: "Upload media, kick off from a Template, or drop a long video and let Claude auto-clip it for you.",
    target: '[data-testid="studio-tab-new"]',
    focusTab: "new",
    accent: "from-nexus-purple to-nexus-pink",
  },
  {
    id: "templates",
    title: "Templates",
    body: "6 collab-ready looks — product reveal, unboxing, tutorial, POV and more. One tap loads the aspect, filter and text preset into the editor.",
    target: '[data-testid="studio-tab-templates"]',
    focusTab: "templates",
    accent: "from-nexus-pink to-amber-400",
  },
  {
    id: "brand-kits",
    title: "Brand Kits",
    body: "Save your colour palette, logo and tagline — every edit stays on-brand without you thinking about it.",
    target: '[data-testid="studio-tab-brand-kits"]',
    focusTab: "brand-kits",
    accent: "from-amber-400 to-emerald-400",
  },
  {
    id: "auto-clips",
    title: "Auto clips",
    body: "Drop a long video (podcast, vlog, livestream) and Claude picks 6 short-form moments — hooks, captions and hashtags included.",
    target: '[data-testid="studio-tab-auto-clips"]',
    focusTab: "auto-clips",
    accent: "from-emerald-400 to-nexus-blue",
  },
  {
    id: "exports",
    title: "Exports",
    body: "Every finished edit shows up here with a shareable link and download button — great for reposting or sending to brands.",
    target: '[data-testid="studio-tab-exports"]',
    focusTab: "exports",
    accent: "from-nexus-blue to-nexus-pink",
  },
];

export function hasSeenCreatorStudioTour() {
  try { return localStorage.getItem(STORAGE_KEY) === "seen"; }
  catch { return false; }
}

export function markCreatorStudioTourSeen() {
  try { localStorage.setItem(STORAGE_KEY, "seen"); } catch { /* ignore */ }
}

export function resetCreatorStudioTour() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

/**
 * Guided walk-through for the /creator-studio page — highlights each tab in turn.
 *
 * Props:
 *   - onClose(): called when the tour is dismissed / finished
 *   - onFocusTab(tabId): switches the parent's active tab before the step renders
 */
export default function CreatorStudioOnboarding({ onClose, onFocusTab }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = STEPS[i];

  // Switch to the step's tab BEFORE we try to locate the target
  useEffect(() => {
    if (step?.focusTab && typeof onFocusTab === "function") {
      onFocusTab(step.focusTab);
    }
  }, [step, onFocusTab]);

  useEffect(() => {
    if (!step?.target) { setRect(null); return; }
    let cancelled = false;
    let attempts = 0;
    const find = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else if (attempts++ < 20) {
        setTimeout(find, 100);
      }
    };
    find();
    const onResize = () => {
      const el = document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    const onScroll = () => onResize();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [step]);

  const finish = () => {
    markCreatorStudioTourSeen();
    onClose?.();
  };

  const next = () => {
    if (i >= STEPS.length - 1) return finish();
    setI(i + 1);
  };

  const skip = () => finish();

  const calloutStyle = (() => {
    if (!rect) return {};
    const margin = 20;
    const calloutW = 340;
    // Prefer below the tab strip
    let left = Math.max(margin, Math.min(window.innerWidth - calloutW - margin, rect.left));
    let top = rect.top + rect.height + margin;
    if (top + 260 > window.innerHeight) {
      // Place above if not enough room below
      top = Math.max(margin, rect.top - 260 - margin);
    }
    return { top, left, width: calloutW };
  })();

  return createPortal(
    <div
      className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-[2px]"
      data-testid="creator-studio-onboarding"
      role="dialog"
      aria-label="Creator Studio walk-through"
    >
      {rect && (
        <div
          className="absolute rounded-xl ring-2 ring-nexus-pink shadow-[0_0_0_9999px_rgba(0,0,0,0.4)] transition-all duration-300 pointer-events-none"
          style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 }}
          data-testid="creator-studio-onboarding-cutout"
        />
      )}

      <div
        className={`absolute glass rounded-2xl p-5 shadow-[0_20px_80px_rgba(0,0,0,0.6)] hairline ${rect ? "" : "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px]"}`}
        style={rect ? calloutStyle : {}}
        data-testid="creator-studio-onboarding-callout"
      >
        <button
          onClick={skip}
          className="absolute top-3 right-3 text-zinc-400 hover:text-white"
          data-testid="creator-studio-onboarding-skip-btn"
          aria-label="Skip tour"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2 mb-3">
          <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${step.accent} flex items-center justify-center`}>
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              Step {i + 1} of {STEPS.length}
              {step.duration && <span className="ml-2 text-nexus-pink">· {step.duration}</span>}
            </div>
            <div className="font-display font-bold text-lg tracking-tight leading-none mt-1">{step.title}</div>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">{step.body}</p>

        <div className="flex items-center gap-1.5 mt-4">
          {STEPS.map((_, idx) => (
            <span
              key={idx}
              className={`h-1 rounded-full transition-all ${idx === i ? "w-6 bg-nexus-pink" : idx < i ? "w-1.5 bg-nexus-pink/40" : "w-1.5 bg-white/15"}`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between mt-5">
          <button
            onClick={skip}
            className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
            data-testid="creator-studio-onboarding-skip-link"
          >
            Skip tour
          </button>
          <button
            onClick={next}
            className="inline-flex items-center gap-1.5 nexus-gradient text-white rounded-full h-9 px-4 font-semibold text-sm"
            data-testid="creator-studio-onboarding-next-btn"
          >
            {i >= STEPS.length - 1 ? "Let's go" : "Next"}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Small icon-only "replay tour" button — pass onClick to reset + open. */
export function CreatorStudioHelpButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="h-9 w-9 rounded-full glass hairline flex items-center justify-center hover:bg-white/5 transition-colors"
      data-testid="creator-studio-help-btn"
      aria-label="Replay Studio tour"
      title="Replay tour"
    >
      <HelpCircle className="h-4 w-4 text-zinc-400" />
    </button>
  );
}
